import type { MemberRowLite } from "@/hooks/spacetime";
import type { Organization } from "@/types/organization";
import type { User } from "@/types/user";

type MembershipRoleStatus = Pick<
  MemberRowLite,
  "organizationId" | "role" | "status"
>;

/**
 * Whether the caller owns `organization`. Trivial, but centralized so every
 * settings page compares the same two fields the same way.
 */
export function isOrgOwner(
  organization: Organization | null | undefined,
  user: User,
): boolean {
  return !!organization && !!user && organization.ownerId === user.id;
}

/**
 * Whether the caller can act as an ADMIN of `organization` — mirrors the
 * reducers' `requireOrgAdmin` gate (packages/spacetime/src/authz.ts): an
 * ACTIVE membership row with role 'admin'. Does NOT include ownership — see
 * `isOrgAdminOrOwner` for the combined check most pages actually want.
 * `memberships` should come from `useMyMemberships()` (caller-scoped; no
 * org_users join needed).
 */
export function isOrgAdmin(
  organization: Organization | null | undefined,
  memberships: MembershipRoleStatus[],
): boolean {
  if (!organization) return false;
  return memberships.some(
    (m) =>
      m.organizationId === organization.id &&
      m.role === "admin" &&
      m.status === "active",
  );
}

/**
 * `isOrgOwner(...) || isOrgAdmin(...)` — the "can manage this organization"
 * check repeated across the settings/nav surface (general, members,
 * sales-tax, subscriptions, danger-zone). In practice every owner also holds
 * an active admin membership row (create_organization/transfer both grant
 * it), so this is a belt-and-suspenders combination rather than two
 * independent paths to the same permission.
 */
export function isOrgAdminOrOwner(
  organization: Organization | null | undefined,
  user: User,
  memberships: MembershipRoleStatus[],
): boolean {
  return (
    isOrgOwner(organization, user) || isOrgAdmin(organization, memberships)
  );
}
